import React, { useEffect, useRef, useState } from "react";
import "../../styles/aboutus.css";
import { aboutUs } from "../../data/aboutUs";
import missionvision from '../../assets/images/aboutus/aboutushero/missionvision.jpg';

const isReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// One simple line icon per value, same stroke style as the carousel arrows
// elsewhere on this page (currentColor, round joins) — matches the little
// pictograms under each "shape" in the reference infographic.
const VALUE_ICONS = [
  // Creativity First — lightbulb
  <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.45 1 1.15 1 1.9V17h5v-1.2c0-.75.4-1.45 1-1.9A6 6 0 0 0 12 3ZM9.5 20h5M10.5 22.5h3" />,
  // Innovation — rocket
  <path d="M14.5 9.5 19 5c.3 2.5-.3 5.4-2.5 7.5M14.5 9.5c-2 2-4 2.7-6 3-.3 2 .4 4 3 6 .3-2 1-4 3-6M14.5 9.5 10.5 5.5c-2.5-.3-5.4.3-7.5 2.5m5 8.5-2 2M9.5 15l-4 1 1-4" />,
  // Integrity — shield with check
  <path d="M12 3.5 19 6v5.5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-2.5Z M9 12l2 2 4-4.5" />,
  // Collaboration — two people
  <path d="M8.5 11a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM16 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM3.5 19c0-2.8 2.2-5 5-5s5 2.2 5 5M14 14.3c1.9.4 3.5 2 3.5 4.2" />,
  // Excellence — star
  <path d="m12 4 2.2 4.9 5.3.6-4 3.7 1.1 5.3L12 15.9 7.4 18.5l1.1-5.3-4-3.7 5.3-.6L12 4Z" />,
];

const AboutUsMissionVision = () => {
  const { mission, vision, values } = aboutUs;
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;

    if (isReducedMotion()) {
      setRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setRevealed(true);
          observer.disconnect();
        });
      },
      // Low threshold on purpose: fires as soon as a sliver of the section
      // is on screen so the fade-in is already running by the time the
      // visitor's eyes get here — no beat of a bare dark section first.
      { threshold: 0.05 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className={`aboutus_mv ${revealed ? "is-revealed" : ""}`} ref={sectionRef}>
      <div className="aboutus_mv_glow" />
      <div className="aboutus_mv_glow aboutus_mv_glow--right" />

      <div className="aboutus_mv_inner">
        {/* Mission and Vision flank one shared image — both fade/slide in
            together the instant the section is reached (see the low
            IntersectionObserver threshold above), not on a delayed cascade. */}
        <div className="aboutus_mv_split">
          <div className="aboutus_mv_split_text aboutus_mv_split_text--left">
            <h2 className="aboutus_mv_title">{mission.title}</h2>
            <span className="aboutus_mv_rule" />
            <p className="aboutus_mv_desc">{mission.description}</p>
          </div>

          <div className="aboutus_mv_split_media">
            <img loading="lazy" decoding="async" src={missionvision} alt="Our Mission and Vision" />
          </div>

          <div className="aboutus_mv_split_text aboutus_mv_split_text--right">
            <h2 className="aboutus_mv_title">{vision.title}</h2>
            <span className="aboutus_mv_rule" />
            <p className="aboutus_mv_desc">{vision.description}</p>
          </div>
        </div>

        <div className="aboutus_values">
          <span className="aboutus_values_label">
            OUR <strong>VALUES</strong>
          </span>

          <div className="aboutus_values_grid">
            {values.map((value, i) => (
              <div
                className={`aboutus_values_item ${revealed ? "is-revealed" : ""}`}
                style={{ transitionDelay: `${0.1 + i * 0.09}s` }}
                key={value.title}
              >
                <span className="aboutus_values_num">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="aboutus_values_title">{value.title}</h3>
                <p className="aboutus_values_desc">{value.description}</p>
                <svg
                  className="aboutus_values_icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {VALUE_ICONS[i % VALUE_ICONS.length]}
                </svg>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUsMissionVision;
